import { BODY_H, BODY_HALF } from "../shared/defs";
import type { PageLink } from "../shared/types";
import type { Sim } from "./sim";
import {
  canonUrl, choosePortal, endSpot, portalBandTop, portalHost, portalLabel, portalShield,
  ELITE_SPAWN_PCT, gateAffixFor, isBossFloor, type GateAffix, type GateStage, type PortalFrame, type PortalSpot,
} from "./portal";
import { hitsScreen, overlapsScreen, placeBaselineLabel, type ScreenRect } from "./screenbox";

interface Mote {
  ang: number;
  speed: number;
  seed: number;
  size: number;
  cyan: boolean;
}

interface LivePortal {
  href: string;
  label: string;
  cx: number;
  cy: number;
  radius: number;
  born: number;
  motes: Mote[];
}

export interface JourneyVisit {
  url: string;
  domain: string;
  destroyed: number;
}

export type { GateAffix, GateStage };
export { gateAffixFor, isBossFloor, gateTitle } from "./portal";

/**
 * A solo run that keeps Challenge's fight and purse, then walks through
 * real links. Level 1 is whatever page the player started. Later levels
 * always come from the proxy.
 */
export class Journey {
  active = false;
  level = 1;
  startUrl = "";
  visits: JourneyVisit[] = [];
  earned = 0;
  /** Failed hops and pages already walked, so the next portal is a new place. */
  readonly blocked = new Set<string>();
  phase: "play" | "suck" | "travel" = "play";
  suck = 0;
  bannerUntil = 0;
  note = "";
  noteUntil = 0;
  /** Level 1's control hint stays hidden after a portal collapse. */
  hintHeld = false;
  /** Edge of the off-screen portal arrow, so bonus toasts can sit clear of its label. */
  arrowEdge: "" | "n" | "s" | "e" | "w" = "";
  deadAt = 0;
  pendingHref = "";
  /** Tester override for the destruction fraction. Null uses the real page. */
  debugFraction: number | null = null;
  /**
   * True once the Journey gate foe is dead. Destroy% only summons the elite/boss;
   * the portal never opens on destruction alone, and there is no stall weak portal.
   */
  qualified = false;
  /** Wreck → gate spawned → gate cleared. */
  gateStage: GateStage = "wreck";
  gateBoss = false;
  gateAffix: GateAffix = "tank";
  portal: LivePortal | null = null;
  private dwell = 0;
  private launching = false;
  private links: PageLink[] = [];
  private pageUrl = "";
  private pageW = 1024;
  private pageH = 800;
  private viewW = 1024;
  private viewH = 800;
  private touch = false;
  private spot: PortalSpot | null = null;
  private seed = 0;
  private world: Sim["world"] | null = null;
  private letters: Sim["letters"] | null = null;

  get open() {
    return !!this.portal;
  }

  get ready() {
    return this.qualified;
  }

  end() {
    if (this.world) this.world.keep = null;
    this.world = null;
    this.letters = null;
    this.active = false;
    this.phase = "play";
    this.portal = null;
    this.launching = false;
    this.suck = 0;
    this.dwell = 0;
    this.deadAt = 0;
    this.pendingHref = "";
    this.note = "";
    this.qualified = false;
    this.gateStage = "wreck";
    this.gateBoss = false;
    this.gateAffix = "tank";
    this.spot = null;
    this.seed = 0;
  }

  begin(url: string) {
    this.end();
    this.active = true;
    this.level = 1;
    this.startUrl = url;
    this.visits = [];
    this.earned = 0;
    this.blocked.clear();
    this.hintHeld = false;
    this.arrowEdge = "";
  }

  arrive(url: string, title: string, links: PageLink[], width: number, height: number, resume: boolean) {
    const domain = portalHost(url || title || "site");
    if (resume && this.visits.length) {
      const last = this.visits[this.visits.length - 1];
      last.url = url;
      last.domain = domain;
      last.destroyed = 0;
    } else {
      this.visits.push({ url, domain, destroyed: 0 });
    }
    this.level = Math.max(1, this.visits.length);
    this.pageUrl = url;
    this.links = links;
    this.pageW = Math.max(1, width);
    this.pageH = Math.max(1, height);
    if (url) this.blocked.add(canonUrl(url));
    this.portal = null;
    this.phase = "play";
    this.suck = 0;
    this.dwell = 0;
    this.launching = false;
    this.pendingHref = "";
    this.deadAt = 0;
    this.bannerUntil = performance.now() + 2800;
    this.qualified = false;
    this.gateStage = "wreck";
    this.gateBoss = isBossFloor(this.level);
    this.gateAffix = gateAffixFor(this.level);
    this.spot = null;
    this.seed = 0;
    this.arrowEdge = "";
  }

  /**
   * Pick the exit as soon as the level exists and keep that link solid.
   * The cells stay in the destruction denominator, so 85% is still reachable.
   */
  plant(sim: Sim, touch: boolean) {
    if (!this.active) return;
    if (this.world && this.world !== sim.world) this.world.keep = null;
    this.world = sim.world;
    this.letters = sim.letters;
    this.viewW = Math.max(1, sim.viewW);
    this.viewH = Math.max(1, sim.viewH);
    this.touch = touch;
    sim.world.keep = null;
    const spot = choosePortal(
      this.links,
      this.pageUrl || this.startUrl,
      this.pageW,
      this.pageH,
      this.blocked,
      Math.random,
      this.frame(),
    );
    this.hold(spot);
  }

  /**
   * Track wreck progress for the elite summon. Portal open still waits on the gate kill.
   * `activeMs` is unused (no stall fallback) but kept so callers stay the same.
   */
  notePlay(_fraction: number, _activeMs = 0) {
    // Portal qualification is gate-kill based. Summoning is driven from main via canSummonGate.
  }

  /** True when destruction is high enough to summon the gate foe. */
  canSummonGate(fraction: number): boolean {
    if (!this.active || this.phase !== "play" || this.portal) return false;
    if (this.gateStage !== "wreck") return false;
    return Math.max(0, Math.min(1, fraction)) >= ELITE_SPAWN_PCT;
  }

  markGateSpawned() {
    if (this.gateStage !== "wreck") return;
    this.gateStage = "spawned";
    this.qualified = false;
    this.note = this.gateBoss ? "Boss appeared" : "Elite appeared";
    this.noteUntil = performance.now() + 2400;
  }

  markGateCleared() {
    if (this.gateStage === "cleared") return;
    this.gateStage = "cleared";
    this.qualified = true;
  }

  hintStage(): "wreck" | "elite" | "boss" {
    if (this.gateStage === "spawned") return this.gateBoss ? "boss" : "elite";
    return "wreck";
  }

  debugSnapshot() {
    return {
      open: this.open,
      qualified: this.qualified,
      gateStage: this.gateStage,
      gateBoss: this.gateBoss,
      gateAffix: this.gateAffix,
      debugFraction: this.debugFraction,
      bandTop: portalBandTop(this.pageH, this.viewH),
      page: { w: this.pageW, h: this.pageH },
      view: { w: this.viewW, h: this.viewH, touch: this.touch },
      spot: this.spot,
      seed: this.seed,
      thresholds: { eliteSpawn: ELITE_SPAWN_PCT },
    };
  }

  private frame(): PortalFrame {
    return { viewW: this.viewW, viewH: this.viewH, touch: this.touch };
  }

  private life(rect: { x: number; y: number; w: number; h: number }): number {
    if (!this.world) return 0;
    return this.world.solidIn(rect) + (this.letters?.covers(rect) ? 1 : 0);
  }

  private hold(spot: PortalSpot) {
    this.spot = spot;
    this.seed = 0;
    if (!this.world || spot.fallback) {
      if (this.world && spot.fallback) this.seat(spot);
      return;
    }
    const shield = portalShield(spot);
    this.seed = this.life(shield);
    if (this.seed > 0) this.world.keep = shield;
  }

  /** Drop a made-up exit onto ground in the last screen when there is some. */
  private seat(spot: PortalSpot) {
    if (!this.world) return;
    const surface = this.world.findSurface(spot.x + 8, spot.x + spot.w - 8, spot.y - 90, spot.y + spot.h + 16);
    if (Number.isNaN(surface)) return;
    const y = surface - spot.h + 4;
    const band = portalBandTop(this.pageH, this.viewH);
    // endSpot already clears the HUD. Only lift onto ground above that, never drop into it.
    if (y > spot.y + 4) return;
    if (y >= band - spot.h && y <= this.pageH - spot.h - 4) spot.y = y;
  }

  seal(fraction: number) {
    const last = this.visits[this.visits.length - 1];
    if (last) last.destroyed = Math.max(0, Math.min(1, fraction));
  }

  grant(n: number) {
    if (this.active && n > 0) this.earned += Math.round(n);
  }

  /** Mean destruction across visited pages, as a percent. Not a sum. */
  pageAverage(): number {
    if (!this.visits.length) return 0;
    const sum = this.visits.reduce((total, visit) => total + visit.destroyed, 0);
    return Math.round((sum / this.visits.length) * 100);
  }

  /** Extra threat above a normal Challenge page. Level 1 stays at 0. */
  pressure(): number {
    return Math.max(0, this.level - 1);
  }

  banner(): string {
    const domain = this.visits[this.visits.length - 1]?.domain || portalHost(this.pageUrl || this.startUrl);
    if (this.gateBoss) return `Level ${this.level}: ${domain} — BOSS`;
    return `Level ${this.level}: ${domain}`;
  }

  /**
   * Spawn the portal for this page. True when a new one just appeared.
   * Only after the gate elite/boss is dead (`qualified`). A reserved link that
   * was lost still yields a spot in the last screen, so the exit cannot fail.
   */
  reveal(): boolean {
    if (!this.active || this.portal || this.phase !== "play" || !this.qualified) return false;
    let spot = this.spot;
    // A hop to this exit already failed (or the page was visited). Pick again.
    if (spot && this.blocked.has(canonUrl(spot.href))) {
      this.spot = null;
      spot = null;
    }
    if (!spot) {
      spot = choosePortal(
        this.links,
        this.pageUrl || this.startUrl,
        this.pageW,
        this.pageH,
        this.blocked,
        Math.random,
        this.frame(),
      );
      this.hold(spot);
    }
    if (!spot.fallback && this.seed > 0 && this.life(portalShield(spot)) <= 0) {
      const lost = spot;
      spot = endSpot(this.pageUrl || this.startUrl, this.pageW, this.pageH, this.blocked, Math.random, this.frame());
      spot.href = lost.href;
      this.seat(spot);
    }
    if (this.world) this.world.keep = null;
    this.spot = spot;
    this.portal = livePortal(spot);
    this.dwell = 0;
    return true;
  }

  cancelSuck() {
    if (this.phase !== "suck") return;
    this.phase = "play";
    this.suck = 0;
    this.dwell = 0;
  }

  collapse() {
    this.phase = "play";
    this.suck = 0;
    this.launching = false;
    this.pendingHref = "";
    this.portal = null;
    this.dwell = 0;
    this.note = "Portal collapsed";
    this.noteUntil = performance.now() + 2400;
    this.hintHeld = true;
  }

  markDead() {
    if (!this.deadAt) this.deadAt = performance.now() + 720;
  }

  /**
   * Pull the player in, then report `enter` once and `hop` when the warp
   * should load the next page.
   */
  tick(sim: Sim, dt: number): "enter" | "hop" | null {
    if (!this.active || !this.portal || this.phase === "travel") return null;
    if (this.phase === "suck") {
      if (!sim.alive) {
        this.cancelSuck();
        return null;
      }
      this.suck += dt;
      const k = Math.min(0.55, dt * 8);
      sim.x += (this.portal.cx - sim.x) * k;
      sim.y += (this.portal.cy + 10 - sim.y) * k;
      sim.vx *= 0.3;
      sim.vy *= 0.3;
      sim.shield = Math.max(sim.shield, 0.25);
      if (this.suck >= 0.7 && !this.launching) {
        this.launching = true;
        this.phase = "travel";
        this.pendingHref = this.portal.href;
        this.blocked.add(canonUrl(this.portal.href));
        return "hop";
      }
      return null;
    }
    if (!sim.alive) {
      this.dwell = 0;
      return null;
    }
    const age = (performance.now() - this.portal.born) / 1000;
    if (age < 0.46) {
      this.dwell = 0;
      return null;
    }
    if (bodyTouches(sim.x, sim.y, this.portal)) this.dwell += dt;
    else this.dwell = 0;
    if (this.dwell < 0.08) return null;
    // Gate elite/boss already dead (portal only reveals then). Full clear is paid by the caller.
    this.phase = "suck";
    this.suck = 0;
    return "enter";
  }

  drawWorld(ctx: CanvasRenderingContext2D, now: number, clip: readonly ScreenRect[] = [], camX = 0, camY = 0, z = 1) {
    if (!this.portal) return;
    ctx.save();
    if (clip.length && z > 0) {
      ctx.beginPath();
      ctx.rect(-1e6, -1e6, 2e6, 2e6);
      const pad = 8 / z;
      for (const box of clip) {
        ctx.rect(camX + box.x / z - pad, camY + box.y / z - pad, box.w / z + pad * 2, box.h / z + pad * 2);
      }
      ctx.clip("evenodd");
    }
    paintPortal(ctx, this.portal, now, this.phase === "suck" ? this.suck / 0.7 : 0);
    ctx.restore();
  }

  drawChrome(ctx: CanvasRenderingContext2D, sim: Sim, now: number, pixel: number, hud: readonly ScreenRect[] = []) {
    this.arrowEdge = "";
    const portal = this.portal;
    if (!portal) {
      if (this.phase === "suck") paintWarp(ctx, now, Math.min(1, this.suck / 0.55));
      return;
    }
    const z = sim.world.Z;
    const sx = (portal.cx - sim.camX) * z;
    const sy = (portal.cy - sim.camY) * z;
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    const screenR = portal.radius * z;
    const seen = mostlyOnScreen(sx, sy, screenR, screenR * 0.72, w, h);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;
    if (seen) {
      const gap = Math.max(4, Math.round(4 * pixel));
      const size = Math.max(12, Math.round(13 * pixel));
      ctx.font = `${size}px daw, monospace`;
      const textW = Math.ceil(ctx.measureText(portal.label).width);
      const padX = Math.round(6 * pixel);
      const boxH = Math.round(18 * pixel);
      const aboveY = sy - screenR * 0.72 - 8 * pixel;
      const belowY = sy + screenR * 0.72 + gap + boxH + 2;
      const anchor = placeBaselineLabel(sx, aboveY, belowY, w, h, textW, padX, boxH, gap, hud);
      paintLabel(ctx, portal.label, anchor.x, anchor.y, pixel);
    } else this.arrowEdge = paintArrow(ctx, sx, sy, w, h, pixel, portal.label.replace(/^→\s*/, ""), sim.world.Z, sim.fight.barLift, hud, now);
    if (this.phase === "suck") paintWarp(ctx, now, Math.min(1, this.suck / 0.55));
    ctx.restore();
  }

  shareText(origin: string): string {
    const lines = this.visits.map((visit, index) => `${index + 1}. ${visit.domain} (${Math.round(visit.destroyed * 100)}%)`);
    const target = this.startUrl || "https://archive.org/";
    const url = `${origin}/?url=${encodeURIComponent(target)}`;
    return [
      `Reached Level ${this.level} in Destroy Any Website — Journey`,
      ...lines,
      `Coins earned: ${this.earned}`,
      `Average per page: ${this.pageAverage()}%`,
      url,
    ].join("\n");
  }
}

function livePortal(spot: PortalSpot): LivePortal {
  const radius = Math.max(36, Math.min(74, Math.max(spot.w, spot.h) * 0.45));
  const motes: Mote[] = [];
  for (let i = 0; i < 28; i++) {
    motes.push({
      ang: (i / 28) * Math.PI * 2 + (i % 3) * 0.2,
      speed: 1.4 + (i % 5) * 0.35,
      seed: (i * 17) % 100 / 100,
      size: i % 4 === 0 ? 3 : 2,
      cyan: i % 2 === 0,
    });
  }
  return {
    href: spot.href,
    label: portalLabel(spot.href),
    cx: spot.x + spot.w / 2,
    cy: spot.y + spot.h / 2,
    radius,
    born: performance.now(),
    motes,
  };
}

function bodyTouches(x: number, y: number, portal: LivePortal): boolean {
  const reach = portal.radius + 10;
  const left = x - (BODY_HALF + 8);
  const right = x + (BODY_HALF + 8);
  const top = y - BODY_H;
  const bottom = y + 4;
  const cx = Math.max(left, Math.min(portal.cx, right));
  const cy = Math.max(top, Math.min(portal.cy, bottom));
  const dx = portal.cx - cx;
  const dy = portal.cy - cy;
  return dx * dx + dy * dy <= reach * reach;
}

function paintPortal(ctx: CanvasRenderingContext2D, portal: LivePortal, now: number, suck: number) {
  const age = Math.max(0, (now - portal.born) / 1000);
  const spawn = Math.min(1, age / 0.42);
  const pop = 1 - (1 - spawn) ** 3;
  const pulse = 0.94 + Math.sin(age * 5.1) * 0.06;
  const r = Math.max(8, portal.radius * pop * pulse * (1 + suck * 0.45));
  ctx.save();
  ctx.translate(portal.cx, portal.cy);
  ctx.imageSmoothingEnabled = false;
  const glow = ctx.createRadialGradient(0, 0, r * 0.15, 0, 0, r * 1.85);
  const alpha = 0.28 + Math.sin(age * 4.2) * 0.08 + suck * 0.25;
  glow.addColorStop(0, `rgba(120, 70, 255, ${alpha.toFixed(3)})`);
  glow.addColorStop(0.42, "rgba(40, 210, 230, 0.18)");
  glow.addColorStop(1, "rgba(40, 210, 230, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.85, r * 1.35, 0, 0, Math.PI * 2);
  ctx.fill();

  const step = 2;
  const reach = Math.ceil(r * 1.12);
  for (let y = -reach; y <= reach; y += step) {
    for (let x = -reach; x <= reach; x += step) {
      const nx = x / r;
      const ny = y / (r * 0.72);
      const dist = Math.hypot(nx, ny);
      if (dist > 1.06 || dist < 0.02) continue;
      const spin = Math.atan2(y, x) + age * (3.1 + suck * 6);
      const band = (Math.sin(spin * 3 - dist * 9 + age * 7) + 1) / 2;
      let color = "#140818";
      if (dist < 0.38) {
        const core = Math.sin(spin * 2 + age * 9 - dist * 12);
        color = core > 0.35 ? "#d8fbff" : core > -0.15 ? "#243056" : "#10061c";
      } else if (dist < 0.72) {
        color = band > 0.62 ? "#4a1d86" : band > 0.28 ? "#2a1050" : "#160824";
      } else if (dist > 0.9) {
        color = band > 0.5 ? "#f4fdff" : "#e4d2ff";
      } else {
        color = band > 0.5 ? "#3ee0ff" : "#a56bff";
      }
      ctx.fillStyle = color;
      ctx.fillRect(x, y, step, step);
    }
  }

  if (spawn < 1) {
    const ring = r * (1.15 + (1 - spawn) * 0.8);
    ctx.strokeStyle = `rgba(180, 245, 255, ${(1 - spawn).toFixed(3)})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(0, 0, ring, ring * 0.72, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  for (const mote of portal.motes) {
    const ang = mote.ang + age * mote.speed * (1 + suck * 1.8);
    const u = (age * 0.42 + mote.seed) % 1;
    const rad = r * (1.22 - u * 1.08);
    const mx = Math.cos(ang) * rad;
    const my = Math.sin(ang) * rad * 0.72;
    ctx.fillStyle = "#140818";
    ctx.fillRect(mx - 2, my - 2, mote.size + 2, mote.size + 2);
    ctx.fillStyle = mote.cyan ? "#e7fdff" : "#f6e6ff";
    ctx.fillRect(mx - 1, my - 1, mote.size, mote.size);
  }
  ctx.restore();
}

function paintLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, pixel: number) {
  const size = Math.max(12, Math.round(13 * pixel));
  ctx.font = `${size}px daw, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  const width = Math.ceil(ctx.measureText(text).width);
  const padX = Math.round(6 * pixel);
  const boxH = Math.round(18 * pixel);
  const left = Math.round(x - width / 2 - padX);
  const top = Math.round(y - boxH);
  ctx.fillStyle = "#140818";
  ctx.fillRect(left - 2, top - 2, width + padX * 2 + 4, boxH + 4);
  ctx.fillStyle = "#241433";
  ctx.fillRect(left, top, width + padX * 2, boxH);
  ctx.fillStyle = "#3ee0ff";
  ctx.fillRect(left, top, width + padX * 2, Math.max(2, Math.round(pixel)));
  ctx.fillStyle = "#f4fbff";
  ctx.fillText(text, Math.round(x), Math.round(y - 2 * pixel));
}

function mostlyOnScreen(sx: number, sy: number, rx: number, ry: number, w: number, h: number): boolean {
  const left = sx - rx;
  const right = sx + rx;
  const top = sy - ry;
  const bottom = sy + ry;
  const ix = Math.max(0, Math.min(w, right) - Math.max(0, left));
  const iy = Math.max(0, Math.min(h, bottom) - Math.max(0, top));
  const area = Math.max(1, (right - left) * (bottom - top));
  return ix * iy >= area * 0.5;
}

function arrowHits(px: number, py: number, reach: number, w: number, h: number, hud: readonly ScreenRect[]): boolean {
  if (px < reach || py < reach || px > w - reach || py > h - reach) return true;
  return hitsScreen({ x: px - reach, y: py - reach, w: reach * 2, h: reach * 2 }, hud, Math.max(6, reach * 0.15));
}

/** Pixel chevron. No circles. Alpha breathes so it stays easy to spot. */
function paintPixelArrow(ctx: CanvasRenderingContext2D, x: number, y: number, ang: number, pixel: number, now: number) {
  const step = Math.max(4, Math.round(5 * Math.max(1, pixel)));
  const pulse = 0.68 + 0.32 * (0.5 + 0.5 * Math.sin(now / 360));
  const cells: Array<[number, number]> = [];
  // Apex points at the portal (+x before rotation); the arms trail back.
  for (let i = 0; i < 5; i++) {
    cells.push([2 - i, -i]);
    cells.push([2 - i, i]);
  }
  for (let i = 0; i < 4; i++) cells.push([-3 + i, 0]);
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.rotate(ang);
  ctx.globalAlpha = pulse;
  ctx.fillStyle = "#140818";
  for (const [cx, cy] of cells) ctx.fillRect(cx * step - step, cy * step - step, step * 3, step * 3);
  ctx.fillStyle = "#f4fbff";
  for (const [cx, cy] of cells) ctx.fillRect(cx * step, cy * step, step, step);
  ctx.fillStyle = "#3ee0ff";
  ctx.fillRect(2 * step, 0, step, step);
  ctx.fillRect(step, -step, step, step);
  ctx.fillRect(step, step, step, step);
  ctx.restore();
}

function paintArrow(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  w: number,
  h: number,
  pixel: number,
  host: string,
  worldZ: number,
  barLift: number,
  hud: readonly ScreenRect[],
  now: number,
): "n" | "s" | "e" | "w" {
  const narrow = w / Math.max(1, pixel) < 520;
  const portrait = h > w;
  const u = Math.max(1, pixel);
  const reach = 24 * u;
  const insetL = 8 * u + reach;
  const insetR = 8 * u + reach;
  // Boss HP sits at the top of the view (label + bar). Keep the chevron below it.
  const bossFloor = (36 + barLift) * worldZ + 10 * u;
  const insetT = Math.max((narrow ? 52 : 36) * u, bossFloor) + reach;
  const insetB = (portrait ? 96 : narrow ? 56 : 64) * u + 12 * u + reach;
  const cx = w / 2;
  const cy = h / 2;
  const dx = px - cx;
  const dy = py - cy;
  let left = insetL;
  let right = Math.max(insetL + reach, w - insetR);
  let top = insetT;
  let bottom = Math.max(insetT + reach, h - insetB);
  if (right - left < reach * 2) {
    left = Math.max(reach, w / 2 - reach);
    right = Math.min(w - reach, w / 2 + reach);
  }
  if (bottom - top < reach * 2) {
    top = Math.max(reach, h / 2 - reach);
    bottom = Math.min(h - reach, h / 2 + reach);
  }
  top = Math.max(top, Math.min(insetT, Math.max(reach, h - reach)));
  if (bottom < top) bottom = Math.min(h - reach, top);
  let tHit = Number.POSITIVE_INFINITY;
  if (dx > 0) tHit = Math.min(tHit, (right - cx) / dx);
  else if (dx < 0) tHit = Math.min(tHit, (left - cx) / dx);
  if (dy > 0) tHit = Math.min(tHit, (bottom - cy) / dy);
  else if (dy < 0) tHit = Math.min(tHit, (top - cy) / dy);
  if (!Number.isFinite(tHit) || tHit < 0) tHit = 0;
  let x = cx + dx * tHit;
  let y = cy + dy * tHit;
  const distT = Math.abs(y - top);
  const distB = Math.abs(y - bottom);
  const distL = Math.abs(x - left);
  const distR = Math.abs(x - right);
  let edge: "n" | "s" | "e" | "w" = "n";
  let best = distT;
  if (distB < best) {
    best = distB;
    edge = "s";
  }
  if (distL < best) {
    best = distL;
    edge = "w";
  }
  if (distR < best) edge = "e";
  const arm = Math.max(22, Math.round(28 * u));
  if (arrowHits(x, y, arm, w, h, hud)) {
    const along = edge === "n" || edge === "s";
    let placed = false;
    for (let i = 1; i <= 32 && !placed; i++) {
      const d = i * Math.max(10, Math.round(arm * 0.4));
      const trials: Array<[number, number]> = along ? [[x - d, y], [x + d, y]] : [[x, y - d], [x, y + d]];
      for (const [tx, ty] of trials) {
        if (arrowHits(tx, ty, arm, w, h, hud)) continue;
        x = tx;
        y = ty;
        placed = true;
        break;
      }
    }
    if (!placed) {
      for (let i = 1; i <= 14 && !placed; i++) {
        const d = i * Math.max(12, Math.round(arm * 0.45));
        let tx = x;
        let ty = y;
        if (edge === "s") ty -= d;
        else if (edge === "n") ty += d;
        else if (edge === "w") tx += d;
        else tx -= d;
        if (arrowHits(tx, ty, arm, w, h, hud)) continue;
        x = tx;
        y = ty;
        placed = true;
      }
    }
    if (!placed) {
      const mids: Array<[number, number, "n" | "s" | "e" | "w"]> = [
        [w / 2, top, "n"],
        [w / 2, bottom, "s"],
        [left, h / 2, "w"],
        [right, h / 2, "e"],
      ];
      for (const [tx, ty, next] of mids) {
        if (arrowHits(tx, ty, arm, w, h, hud)) continue;
        x = tx;
        y = ty;
        edge = next;
        break;
      }
    }
  }
  const ang = Math.atan2(py - y, px - x);
  paintPixelArrow(ctx, x, y, ang, u, now);
  if (!host) return edge;
  const size = Math.max(12, Math.round(13 * u));
  ctx.font = `${size}px daw, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const ndx = cx - x;
  const ndy = cy - y;
  const nlen = Math.hypot(ndx, ndy) || 1;
  const width = Math.ceil(ctx.measureText(host).width);
  const boxH = Math.round(size * 1.35);
  const pad = 4 * u;
  const boxW = width + pad * 2;
  const gap = Math.max(4, Math.round(4 * u));
  const ux = ndx / nlen;
  const uy = ndy / nlen;
  const chevron: ScreenRect = { x: x - arm, y: y - arm, w: arm * 2, h: arm * 2 };
  const labelBox = (x0: number, y0: number): ScreenRect => ({
    x: Math.round(x0 - boxW / 2) - 2,
    y: Math.round(y0 - boxH / 2) - 2,
    w: boxW + 4,
    h: boxH + 4,
  });
  const clampX = (v: number) => Math.max(8 * u + width / 2, Math.min(w - 8 * u - width / 2, v));
  const clampY = (v: number) => Math.max(bossFloor + boxH / 2, Math.min(h - size, v));
  const fits = (x0: number, y0: number) => {
    const box = labelBox(x0, y0);
    return !overlapsScreen(box, chevron, gap) && !hitsScreen(box, hud, gap);
  };
  // Toward the middle of the screen first, clear of the chevron; then beside it.
  const clear = arm + gap + Math.abs(ux) * (boxW / 2) + Math.abs(uy) * (boxH / 2 + 2);
  const tries: Array<[number, number]> = [];
  for (const k of [1, 1.6, 2.3]) {
    const bx = x + ux * clear * k;
    const by = y + uy * clear * k;
    tries.push([bx, by]);
    for (let i = 1; i <= 6; i++) {
      const d = i * Math.max(14, boxH);
      if (Math.abs(uy) >= Math.abs(ux)) tries.push([bx + d, by], [bx - d, by]);
      else tries.push([bx, by + d], [bx, by - d]);
    }
  }
  const side = arm + gap + boxW / 2 + 2;
  tries.push([x + side, y], [x - side, y], [x, y - arm - gap - boxH / 2 - 2], [x, y + arm + gap + boxH / 2 + 2]);
  let lx = clampX(x + ux * clear);
  let ly = clampY(y + uy * clear);
  for (const [tx, ty] of tries) {
    const fx = clampX(tx);
    const fy = clampY(ty);
    if (!fits(fx, fy)) continue;
    lx = fx;
    ly = fy;
    break;
  }
  ctx.fillStyle = "#140818";
  ctx.fillRect(Math.round(lx - boxW / 2) - 2, Math.round(ly - boxH / 2) - 2, boxW + 4, boxH + 4);
  ctx.fillStyle = "#241433";
  ctx.fillRect(Math.round(lx - boxW / 2), Math.round(ly - boxH / 2), boxW, boxH);
  ctx.fillStyle = "#f4fbff";
  ctx.fillText(host, Math.round(lx), Math.round(ly));
  return edge;
}

/** Opaque pixel tunnel used while a Journey portal is carrying the player. */
export function paintWarp(ctx: CanvasRenderingContext2D, now: number, cover: number) {
  const amount = Math.max(0, Math.min(1, cover));
  if (amount <= 0.02) return;
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const t = now / 1000;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = `rgba(7, 4, 16, ${amount.toFixed(3)})`;
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = amount;
  const cx = w / 2;
  const cy = h / 2;
  const cell = Math.max(3, Math.round(Math.min(w, h) / 120));
  const maxR = Math.hypot(cx, cy);
  for (let ring = 2; ring < 16; ring++) {
    const r = (ring / 16) * maxR;
    const count = 10 + ring * 3;
    const spin = t * (1.4 + ring * 0.18) * (ring % 2 ? 1 : -1);
    for (let i = 0; i < count; i++) {
      const a = spin + (i / count) * Math.PI * 2;
      const pull = 1 - ((t * 0.35 + ring * 0.04) % 1) * 0.18;
      const x = cx + Math.cos(a) * r * pull;
      const y = cy + Math.sin(a) * r * 0.62 * pull;
      const band = (i + ring + Math.floor(t * 6)) % 4;
      ctx.fillStyle = band === 0 ? "#f4fbff" : band === 1 ? "#3ee0ff" : band === 2 ? "#a56bff" : "#4a1d86";
      ctx.fillRect((x / cell | 0) * cell, (y / cell | 0) * cell, cell, cell);
    }
  }
  const core = cell * 4;
  ctx.fillStyle = "#140818";
  ctx.fillRect(cx - core, cy - core, core * 2, core * 2);
  ctx.fillStyle = "#3ee0ff";
  ctx.fillRect(cx - core + cell, cy - cell, core * 2 - cell * 2, cell * 2);
  ctx.restore();
}

